import { useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@api'
import { appendAvatarToFormData } from '../../../utils/fileUpload'
import { showSuccessToast } from '@utils'
import { useLanguage } from '@context'

export interface UpdateProfileData {
  firstName?: string
  lastName?: string
  bio?: string
  birthDate?: Date | string
  gender?: string
  hashtags?: string[]
  goals?: string[]
  hobbies?: string[]
  skills?: string[]
  contacts?: {
    email?: string | null
    phone?: string | null
    website?: string | null
    facebook?: string | null
    instagram?: string | null
    linkedin?: string | null
    twitter?: string | null
  }
  professionIds?: string[]
  avatar?: {
    uri: string
    type?: string
  },
  languages?: string[]
  nationalities?: string[]
  origins?: string[]
  residence?: string
  residenceHistory?: string[]
  latitude?: number | null
  longitude?: number | null
  language?: string
  /** When true, skip success toast (e.g. for background coord updates) */
  _silent?: boolean
  availabilities?: {
    momentIds: string[]
    slots: Array<{
      days: number[]
      from: string
      to: string
    }>
  }
}

export const updateProfile = async (data: UpdateProfileData): Promise<void> => {
  const { avatar, contacts, professionIds, languages, nationalities, origins, residence, residenceHistory, availabilities, latitude, longitude, language, _silent, ...updateData } = data
  const formData = new FormData()

  // Add simple fields (based on UpdateProfileHttpRequestBody structure)
  if (updateData.firstName) {
    formData.append('firstName', updateData.firstName)
  }
  if (updateData.lastName) {
    formData.append('lastName', updateData.lastName)
  }
  if (updateData.bio !== undefined) {
    formData.append('bio', updateData.bio)
  }
  if (updateData.birthDate) {
    formData.append('birthDate', updateData.birthDate instanceof Date 
      ? updateData.birthDate.toISOString() 
      : updateData.birthDate)
  }
  if (updateData.gender) {
    formData.append('gender', updateData.gender)
  }
  if (updateData.hashtags !== undefined) {
    if (updateData.hashtags.length > 0) {
      updateData.hashtags.forEach((tag) => {
        formData.append('hashtags[]', tag)
      })
    } else {
      formData.append('hashtags', JSON.stringify([]))
    }
  }

  // Handle goals, hobbies, skills like hashtags
  if (updateData.goals !== undefined) {
    if (updateData.goals.length > 0) {
      updateData.goals.forEach((goal) => {
        formData.append('goals[]', goal)
      })
    } else {
      formData.append('goals', JSON.stringify([]))
    }
  }

  if (updateData.hobbies !== undefined) {
    if (updateData.hobbies.length > 0) {
      updateData.hobbies.forEach((hobby) => {
        formData.append('hobbies[]', hobby)
      })
    } else {
      formData.append('hobbies', JSON.stringify([]))
    }
  }

  if (updateData.skills !== undefined) {
    if (updateData.skills.length > 0) {
      updateData.skills.forEach((skill) => {
        formData.append('skills[]', skill)
      })
    } else {
      formData.append('skills', JSON.stringify([]))
    }
  }

  // Handle contacts as JSON string
  if (contacts !== undefined) {
    formData.append('contacts', JSON.stringify(contacts))
  }

  // Handle professionIds
  if (professionIds !== undefined) {
    if (professionIds.length > 0) {
      professionIds.forEach((id) => {
        formData.append('professionIds[]', id)
      })
    } else {
      formData.append('professionIds', JSON.stringify([]))
    }
  }

  // Handle residenceHistory
  if (residenceHistory !== undefined) {
    const residenceHistoryArray = residenceHistory.map(residence => ({
      city: residence,
      since: new Date().toISOString(),
      until: null,
      address: '',
      country: null,
      isCurrent: false,
    }))
    formData.append('residenceHistory', JSON.stringify(residenceHistoryArray))
  }

  // Handle avatar
  if (avatar) {
    appendAvatarToFormData(formData, avatar.uri, 'avatar')
  }

  if (languages) {
    if (languages.length > 0) {
      languages.forEach((language, index) => {
        formData.append(`languages[${index}]`, JSON.stringify({ code: language, level: 'fluent' }))
      })
    } else {
      formData.append('languages', JSON.stringify([]))
    }
  }
  
  if (nationalities) {
    if (nationalities.length > 0) {
      nationalities.forEach((nationality, index) => {
        formData.append(`nationalities[${index}]`, nationality)
      })
    } else {
      formData.append('nationalities', JSON.stringify([]))
    }
  }
  
  if (origins) {
    if (origins.length > 0) {
      origins.forEach((origin, index) => {
        formData.append(`origins[${index}]`, origin)
      })
    } else {
      formData.append('origins', JSON.stringify([]))
    }
  }
  if (residence) {
    formData.append('residence', residence)
  }
  if (latitude != null) {
    formData.append('latitude', String(latitude))
  }
  if (longitude != null) {
    formData.append('longitude', String(longitude))
  }

  // Handle availabilities
  if (availabilities !== undefined) {
    formData.append('availabilities', JSON.stringify(availabilities))
  }

  if (language) {
    formData.append('language', language)
  }

  await api.patch('/profile/me', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  })
}

export const useUpdateProfile = () => {
  const queryClient = useQueryClient()
  const { t } = useLanguage()

  return useMutation<void, Error, UpdateProfileData>({
    mutationFn: updateProfile,
    onSuccess: async (_, variables) => {
      if (!variables._silent) {
        showSuccessToast(t('toast.success'), t('profile.updateSuccess'))
      }
      await queryClient.invalidateQueries({ queryKey: ['profile', 'me'] })
    },
  })
}

